import type { WorkflowGraph, WorkflowGraphConnection, WorkflowGraphNode } from "@/types/leadflow"
import { normalizeConditionConfig } from "./variables/registry.ts"

export function blankWorkflowGraph(): WorkflowGraph {
  return { schema_version: 1, nodes: [], connections: [] }
}

export function normalizeWorkflowGraph(value: unknown): WorkflowGraph {
  const graph = isPlainObject(value) ? value as Record<string, unknown> : {}
  return {
    schema_version: normalizeSchemaVersion(graph.schema_version),
    nodes: Array.isArray(graph.nodes) ? graph.nodes.map(normalizeNode).filter(Boolean) as WorkflowGraphNode[] : [],
    connections: Array.isArray(graph.connections) ? graph.connections.map(normalizeConnection).filter(Boolean) as WorkflowGraphConnection[] : [],
  }
}

export function isEmptyWorkflowGraph(graph: WorkflowGraph | null | undefined) {
  return !graph || graph.nodes.length === 0 && graph.connections.length === 0
}

export function graphHasDraftChanges(graph: WorkflowGraph | null | undefined, published: WorkflowGraph | null | undefined) {
  return workflowGraphDigest(graph) !== workflowGraphDigest(published)
}

export function workflowGraphDigest(graph: WorkflowGraph | null | undefined) {
  return JSON.stringify(canonicalWorkflowGraph(graph ?? blankWorkflowGraph()))
}

export type WorkflowGraphDifference = {
  path: string
  baselineValue: unknown
  liveValue: unknown
  baselineType: string
  liveType: string
}

export function firstGraphDifference(baseline: WorkflowGraph | null | undefined, live: WorkflowGraph | null | undefined): WorkflowGraphDifference | null {
  return firstDifference(canonicalWorkflowGraph(baseline ?? blankWorkflowGraph()), canonicalWorkflowGraph(live ?? blankWorkflowGraph()), "graph")
}

export function canonicalWorkflowGraph(value: WorkflowGraph | null | undefined): WorkflowGraph {
  const normalized = normalizeWorkflowGraph(value ?? blankWorkflowGraph())
  return {
    schema_version: normalized.schema_version,
    nodes: normalized.nodes
      .map((node) => ({ ...node, config: deepSort(canonicalConfig(node.type, node.config)) as Record<string, unknown> }))
      .sort((a, b) => a.id.localeCompare(b.id) || a.type.localeCompare(b.type)),
    connections: normalized.connections
      .map((connection) => ({ ...connection }))
      .sort((a, b) => [a.sourceNodeId, a.sourcePort, a.targetNodeId, a.targetPort, a.id].join("|").localeCompare([b.sourceNodeId, b.sourcePort, b.targetNodeId, b.targetPort, b.id].join("|"))),
  }
}

export function cloneWorkflowGraph(graph: WorkflowGraph): WorkflowGraph {
  return normalizeWorkflowGraph(JSON.parse(JSON.stringify(graph)))
}

export function createNodeId(type: string) {
  const base = type.replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "").toLowerCase()
  const suffix = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID().slice(0, 8) : Math.random().toString(36).slice(2, 8)
  return `${base || "node"}_${suffix}`
}

function normalizeNode(value: unknown): WorkflowGraphNode | null {
  if (!isPlainObject(value)) return null
  const node = value as Record<string, unknown>
  const id = stringValue(node.id)
  const type = stringValue(node.type)
  if (!id || !type) return null
  return {
    id,
    type,
    position: normalizePosition(node.position),
    config: isPlainObject(node.config) ? node.config as Record<string, unknown> : {},
  }
}

function normalizeConnection(value: unknown): WorkflowGraphConnection | null {
  if (!isPlainObject(value)) return null
  const connection = value as Record<string, unknown>
  const id = stringValue(connection.id)
  const sourceNodeId = stringValue(connection.sourceNodeId)
  const sourcePort = stringValue(connection.sourcePort)
  const targetNodeId = stringValue(connection.targetNodeId)
  const targetPort = stringValue(connection.targetPort)
  if (!id || !sourceNodeId || !sourcePort || !targetNodeId || !targetPort) return null
  return { id, sourceNodeId, sourcePort, targetNodeId, targetPort }
}

function normalizePosition(value: unknown) {
  if (!isPlainObject(value)) return { x: 0, y: 0 }
  const position = value as Record<string, unknown>
  return { x: positionNumberValue(position.x), y: positionNumberValue(position.y) }
}

function normalizeSchemaVersion(value: unknown) {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1
}

function stringValue(value: unknown) {
  return typeof value === "string" ? value.trim() : value == null ? "" : String(value).trim()
}

function numberValue(value: unknown) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function positionNumberValue(value: unknown) {
  const parsed = numberValue(value)
  return Math.round(parsed * 1000) / 1000
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value)
}

function canonicalConfig(type: string, config: Record<string, unknown>) {
  const next = { ...config }
  switch (type) {
    case "condition":
      return normalizeConditionConfig(next, [])
    case "schedule.trigger":
      return normalizeNumericKey(next, "schedule_interval_minutes")
    case "task.create":
    case "create_follow_up":
      return normalizeNumericKey(next, "due_in_minutes")
    case "delay":
      return normalizeNumericKey(next, "minutes")
    case "create_internal_note":
      if ("pinned" in next) next.pinned = next.pinned === true || next.pinned === "true"
      return next
    default:
      return normalizeScalarValues(next)
  }
}

function normalizeNumericKey(config: Record<string, unknown>, key: string) {
  if (key in config) config[key] = numberValue(config[key])
  return normalizeScalarValues(config)
}

function normalizeScalarValues(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(normalizeScalarValues)
  if (isPlainObject(value)) {
    return Object.keys(value).reduce<Record<string, unknown>>((memo, key) => {
      const item = value[key]
      memo[key] = normalizeScalarValues(item)
      return memo
    }, {})
  }
  return value
}

function deepSort(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(deepSort)
  if (isPlainObject(value)) {
    return Object.keys(value).sort().reduce<Record<string, unknown>>((memo, key) => {
      memo[key] = deepSort(value[key])
      return memo
    }, {})
  }
  return value
}

function firstDifference(baseline: unknown, live: unknown, path: string): WorkflowGraphDifference | null {
  if (Object.is(baseline, live)) return null
  if (Array.isArray(baseline) && Array.isArray(live)) {
    const baselineKeyed = keyedArray(baseline)
    const liveKeyed = keyedArray(live)
    if (baselineKeyed && liveKeyed) {
      const keys = Array.from(new Set([...baselineKeyed.keys(), ...liveKeyed.keys()])).sort()
      for (const key of keys) {
        if (!baselineKeyed.has(key)) return { path: `${path}[${key}]`, baselineValue: undefined, liveValue: liveKeyed.get(key), baselineType: "undefined", liveType: valueType(liveKeyed.get(key)) }
        if (!liveKeyed.has(key)) return { path: `${path}[${key}]`, baselineValue: baselineKeyed.get(key), liveValue: undefined, baselineType: valueType(baselineKeyed.get(key)), liveType: "undefined" }
        const nested = firstDifference(baselineKeyed.get(key), liveKeyed.get(key), `${path}[${key}]`)
        if (nested) return nested
      }
      return null
    }
    const length = Math.max(baseline.length, live.length)
    for (let index = 0; index < length; index += 1) {
      const nested = firstDifference(baseline[index], live[index], `${path}[${index}]`)
      if (nested) return nested
    }
    return null
  }
  if (isPlainObject(baseline) && isPlainObject(live)) {
    const keys = Array.from(new Set([...Object.keys(baseline), ...Object.keys(live)])).sort()
    for (const key of keys) {
      const nested = firstDifference(baseline[key], live[key], path === "graph" ? key : `${path}.${key}`)
      if (nested) return nested
    }
    return null
  }
  return { path, baselineValue: baseline, liveValue: live, baselineType: valueType(baseline), liveType: valueType(live) }
}

function valueType(value: unknown) {
  if (value === null) return "null"
  if (Array.isArray(value)) return "array"
  return typeof value
}

function keyedArray(value: unknown[]) {
  const keys = value.map((item) => itemKey(item))
  if (keys.some((key) => !key) || new Set(keys).size !== keys.length) return null
  return new Map(keys.map((key, index) => [key as string, value[index]]))
}

function itemKey(item: unknown) {
  if (!isPlainObject(item)) return null
  if (typeof item.id === "string" && item.id) return item.id
  if ("sourceNodeId" in item && "sourcePort" in item && "targetNodeId" in item && "targetPort" in item) {
    return [item.sourceNodeId, item.sourcePort, item.targetNodeId, item.targetPort, item.id].map(String).join("|")
  }
  return null
}
